import {
  Users, Handshake, DollarSign, CheckSquare, Building2,
  UserPlus, AlertTriangle, CheckCircle2, Clock, ListTodo,
  ArrowUpRight, CircleDot,
} from 'lucide-react';
import { useDashboard } from '@/composables/dashboard/useDashboard';
import { PageContainer } from '@/components/layout/PageContainer';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { AreaChartComponent } from '@/components/charts/AreaChart';
import { PieChartComponent } from '@/components/charts/PieChart';
import { Skeleton } from '@/components/ui/skeleton';
import { formatCurrency, formatNumber, formatPercentage, formatRelativeTime } from '@/lib/formatters';
import { StatusBadge } from '@/components/StatusBadge';

function getStageColor(color?: string, index?: number) {
  if (color) return color;
  const palette = ['#3b82f6', '#6366f1', '#8b5cf6', '#f59e0b', '#ef4444'];
  return palette[(index ?? 0) % palette.length];
}

export function DashboardPage() {
  const {
    summary, summaryLoading,
    dealsFunnel, revenueChart,
    leadSources, tasksOverview,
    recentActivities, topDeals,
  } = useDashboard();

  // Parse deals funnel stages
  const funnelStages = (Array.isArray(dealsFunnel) ? dealsFunnel : (dealsFunnel as any)?.stages ?? []) as any[];

  // Parse tasks overview into a map
  const taskMap: Record<string, number> = {};
  (Array.isArray(tasksOverview) ? tasksOverview : []).forEach((t: any) => {
    taskMap[t.status] = t.count;
  });
  const totalTasks = Object.values(taskMap).reduce((a, b) => a + b, 0);

  return (
    <PageContainer title="Dashboard" description="Overview of your operations">
      {/* KPI Cards - Row 1 */}
      <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Total Leads</CardTitle>
            <Users className="h-4 w-4 text-blue-600" />
          </CardHeader>
          <CardContent>
            {summaryLoading ? <Skeleton className="h-8 w-24" /> : (
              <>
                <div className="text-2xl font-bold">{formatNumber(summary?.totalLeads ?? 0)}</div>
                <p className="text-xs text-muted-foreground mt-1">
                  <span className="text-blue-600 font-medium">+{summary?.newLeadsThisMonth ?? 0}</span> this month
                  {' '}&middot;{' '}{formatPercentage(summary?.leadConversionRate ?? 0)} converted
                </p>
              </>
            )}
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Pipeline Value</CardTitle>
            <Handshake className="h-4 w-4 text-green-600" />
          </CardHeader>
          <CardContent>
            {summaryLoading ? <Skeleton className="h-8 w-24" /> : (
              <>
                <div className="text-2xl font-bold">{formatCurrency(summary?.pipelineValue ?? 0)}</div>
                <p className="text-xs text-muted-foreground mt-1">
                  <span className="text-green-600 font-medium">{summary?.openDeals ?? 0}</span> open deals
                  {' '}&middot;{' '}{formatPercentage(summary?.dealWinRate ?? 0)} win rate
                </p>
              </>
            )}
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Revenue Won</CardTitle>
            <DollarSign className="h-4 w-4 text-purple-600" />
          </CardHeader>
          <CardContent>
            {summaryLoading ? <Skeleton className="h-8 w-24" /> : (
              <>
                <div className="text-2xl font-bold">{formatCurrency(summary?.totalRevenue ?? 0)}</div>
                <p className="text-xs text-muted-foreground mt-1">
                  <span className="text-purple-600 font-medium">{formatCurrency(summary?.revenueThisMonth ?? 0)}</span> this month
                  {' '}&middot;{' '}{summary?.wonDeals ?? 0} deals won
                </p>
              </>
            )}
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Open Tasks</CardTitle>
            <CheckSquare className="h-4 w-4 text-orange-600" />
          </CardHeader>
          <CardContent>
            {summaryLoading ? <Skeleton className="h-8 w-24" /> : (
              <>
                <div className="text-2xl font-bold">{formatNumber(summary?.openTasks ?? 0)}</div>
                <p className="text-xs text-muted-foreground mt-1">
                  {(summary?.overdueTasks ?? 0) > 0 ? (
                    <span className="text-red-500 font-medium">{summary?.overdueTasks} overdue</span>
                  ) : (
                    <span className="text-green-600 font-medium">No overdue</span>
                  )}
                  {' '}&middot;{' '}{summary?.completedTasksThisMonth ?? 0} completed this month
                </p>
              </>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Secondary KPI - Row 2 */}
      <div className="grid gap-4 md:grid-cols-3">
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center gap-3">
              <div className="p-2 rounded-lg bg-blue-100 dark:bg-blue-900/30"><UserPlus className="h-5 w-5 text-blue-600" /></div>
              <div>
                <p className="text-sm text-muted-foreground">Contacts</p>
                <p className="text-xl font-bold">{summaryLoading ? '...' : formatNumber(summary?.totalContacts ?? 0)}</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center gap-3">
              <div className="p-2 rounded-lg bg-green-100 dark:bg-green-900/30"><Building2 className="h-5 w-5 text-green-600" /></div>
              <div>
                <p className="text-sm text-muted-foreground">Companies</p>
                <p className="text-xl font-bold">{summaryLoading ? '...' : formatNumber(summary?.totalCompanies ?? 0)}</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center gap-3">
              <div className="p-2 rounded-lg bg-purple-100 dark:bg-purple-900/30"><ArrowUpRight className="h-5 w-5 text-purple-600" /></div>
              <div>
                <p className="text-sm text-muted-foreground">Converted Leads</p>
                <p className="text-xl font-bold">{summaryLoading ? '...' : formatNumber(summary?.convertedLeads ?? 0)}</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Charts Row */}
      <div className="grid gap-4 lg:grid-cols-2">
        {/* Revenue Chart */}
        <AreaChartComponent
          title="Revenue Trend"
          data={(Array.isArray(revenueChart) ? revenueChart : []).map((item: any) => ({
            date: item.month || item.date,
            Revenue: item.revenue ?? 0,
          }))}
          dataKey="Revenue"
          xAxisKey="date"
        />

        {/* Deal Pipeline - Cards */}
        <Card>
          <CardHeader>
            <CardTitle className="text-base">Deal Pipeline</CardTitle>
          </CardHeader>
          <CardContent>
            {funnelStages.length === 0 ? (
              <p className="text-sm text-muted-foreground text-center py-8">No pipeline data</p>
            ) : (
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
                {funnelStages.map((s: any, i: number) => (
                  <div key={s.stageId || i} className="rounded-lg border p-3 space-y-1">
                    <div className="flex items-center gap-2">
                      <div className="h-2.5 w-2.5 rounded-full shrink-0" style={{ backgroundColor: getStageColor(s.color, i) }} />
                      <span className="text-xs font-medium text-muted-foreground truncate">{s.stageName || s.stage}</span>
                    </div>
                    <p className="text-xl font-bold">{s.count}</p>
                    <p className="text-xs text-muted-foreground">{formatCurrency(s.totalValue ?? s.value ?? 0)}</p>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Lead Sources + Tasks Overview */}
      <div className="grid gap-4 lg:grid-cols-2">
        {/* Lead Sources */}
        {(Array.isArray(leadSources) ? leadSources : []).length > 0 ? (
          <PieChartComponent
            title="Lead Sources"
            data={(Array.isArray(leadSources) ? leadSources : []).map((s: any) => ({
              name: (s.source || '').replace(/_/g, ' '),
              value: s.count,
            }))}
          />
        ) : (
          <Card>
            <CardHeader><CardTitle className="text-base">Lead Sources</CardTitle></CardHeader>
            <CardContent><p className="text-sm text-muted-foreground text-center py-8">No lead data yet</p></CardContent>
          </Card>
        )}

        {/* Tasks Overview - Cards */}
        <Card>
          <CardHeader>
            <CardTitle className="text-base">Tasks Overview</CardTitle>
          </CardHeader>
          <CardContent>
            {totalTasks === 0 ? (
              <p className="text-sm text-muted-foreground text-center py-8">No tasks yet</p>
            ) : (
              <div className="grid grid-cols-2 gap-3">
                <div className="rounded-lg border p-3 space-y-1">
                  <div className="flex items-center gap-2">
                    <ListTodo className="h-4 w-4 text-blue-500" />
                    <span className="text-xs font-medium text-muted-foreground">To Do</span>
                  </div>
                  <p className="text-xl font-bold">{taskMap['TODO'] ?? 0}</p>
                </div>
                <div className="rounded-lg border p-3 space-y-1">
                  <div className="flex items-center gap-2">
                    <Clock className="h-4 w-4 text-yellow-500" />
                    <span className="text-xs font-medium text-muted-foreground">In Progress</span>
                  </div>
                  <p className="text-xl font-bold">{taskMap['IN_PROGRESS'] ?? 0}</p>
                </div>
                <div className="rounded-lg border p-3 space-y-1">
                  <div className="flex items-center gap-2">
                    <CheckCircle2 className="h-4 w-4 text-green-500" />
                    <span className="text-xs font-medium text-muted-foreground">Completed</span>
                  </div>
                  <p className="text-xl font-bold">{taskMap['COMPLETED'] ?? 0}</p>
                </div>
                <div className="rounded-lg border p-3 space-y-1">
                  <div className="flex items-center gap-2">
                    <AlertTriangle className="h-4 w-4 text-red-500" />
                    <span className="text-xs font-medium text-muted-foreground">Cancelled</span>
                  </div>
                  <p className="text-xl font-bold">{taskMap['CANCELLED'] ?? 0}</p>
                </div>
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Bottom Row */}
      <div className="grid gap-4 lg:grid-cols-2">
        {/* Top Deals */}
        <Card>
          <CardHeader>
            <CardTitle className="text-base">Top Deals</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              {topDeals.slice(0, 8).map((deal) => (
                <div key={deal.id} className="flex items-center justify-between">
                  <div className="min-w-0 flex-1">
                    <p className="text-sm font-medium truncate">{deal.title}</p>
                    <p className="text-xs text-muted-foreground">{deal.company?.name || 'No company'} {deal.stage?.name ? `\u00b7 ${deal.stage.name}` : ''}</p>
                  </div>
                  <div className="text-right ml-3 shrink-0">
                    <p className="text-sm font-semibold">{formatCurrency(deal.value)}</p>
                    <StatusBadge status={deal.status} type="deal" />
                  </div>
                </div>
              ))}
              {topDeals.length === 0 && <p className="text-sm text-muted-foreground text-center py-4">No deals yet</p>}
            </div>
          </CardContent>
        </Card>

        {/* Recent Activities */}
        <Card>
          <CardHeader>
            <CardTitle className="text-base">Recent Activities</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              {recentActivities.slice(0, 10).map((activity) => (
                <div key={activity.id} className="flex items-start gap-3">
                  <div className="mt-1 shrink-0"><CircleDot className="h-3 w-3 text-primary" /></div>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium truncate">{activity.subject}</p>
                    <p className="text-xs text-muted-foreground">
                      {activity.type?.replace(/_/g, ' ')} &middot; {formatRelativeTime(activity.occurredAt)}
                    </p>
                  </div>
                </div>
              ))}
              {recentActivities.length === 0 && <p className="text-sm text-muted-foreground text-center py-4">No recent activities</p>}
            </div>
          </CardContent>
        </Card>
      </div>
    </PageContainer>
  );
}
